import { baseKeymap, chainCommands, toggleMark } from "prosemirror-commands";
import { history, redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { splitListItem } from "prosemirror-schema-list";
import { EditorState, Plugin, Transaction } from "prosemirror-state";
import { Schema } from "prosemirror-model";
import {
  defaultMarkdownParser,
  defaultMarkdownSerializer,
  schema as markdownSchema,
  MarkdownParser,
  MarkdownSerializer,
} from "prosemirror-markdown";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ProseMirror,
  ProseMirrorDoc,
  useEditorEventCallback,
  reactKeys,
} from "@handlewithcare/react-prosemirror";
import SimpleEditorMenu from "./SimpleEditorMenu";
import FlowchartEditorModal from "@/components/advisor/flowchart/FlowchartEditorModal";
import { createMermaidNodeViewPlugin } from "@/components/advisor/flowchart/MermaidNodeView";
import { authenticatedBitswanBackendInstance } from "@/lib/api-client";
import MarkdownIt from "markdown-it";

// Extend the default markdown schema with an image node
const editorSchema = new Schema({
  nodes: markdownSchema.spec.nodes.update("image", {
    inline: true,
    attrs: {
      src: {},
      alt: { default: null },
      title: { default: null },
    },
    group: "inline",
    draggable: true,
    parseDOM: [
      {
        tag: "img[src]",
        getAttrs(dom: HTMLElement) {
          return {
            src: dom.getAttribute("src"),
            alt: dom.getAttribute("alt"),
            title: dom.getAttribute("title"),
          };
        },
      },
    ],
    toDOM(node) {
      return ["img", {
        src: node.attrs.src,
        alt: node.attrs.alt || "",
        title: node.attrs.title || undefined,
        style: "max-width: 100%; height: auto;",
      }];
    },
  }),
  marks: markdownSchema.spec.marks,
});

// Build markdown parser that handles images
const editorMarkdownParser = new MarkdownParser(
  editorSchema,
  MarkdownIt("commonmark", { html: false }),
  {
    ...defaultMarkdownParser.tokens,
    image: {
      node: "image",
      getAttrs: (tok: any) => ({
        src: tok.attrGet("src"),
        alt: tok.children?.[0]?.content || null,
        title: tok.attrGet("title") || null,
      }),
    },
  }
);

// Build markdown serializer that outputs ![alt](src "title")
const editorMarkdownSerializer = new MarkdownSerializer(
  {
    ...defaultMarkdownSerializer.nodes,
    image(state, node) {
      state.write(
        "![" +
          state.esc(node.attrs.alt || "") +
          "](" +
          node.attrs.src +
          (node.attrs.title ? ' "' + node.attrs.title.replace(/"/g, '\\"') + '"' : "") +
          ")"
      );
    },
  },
  defaultMarkdownSerializer.marks
);

async function uploadImage(file: File): Promise<string> {
  const api = await authenticatedBitswanBackendInstance();
  const formData = new FormData();
  formData.append("image", file);
  const resp = await api.post("/advisor/content-images/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return resp.data.url;
}

function createImagePastePlugin(schema: Schema): Plugin {
  return new Plugin({
    props: {
      handleDOMEvents: {
        paste(view, event) {
          const items = event.clipboardData?.items;
          if (!items) return false;
          for (const item of Array.from(items)) {
            if (item.type.startsWith("image/")) {
              event.preventDefault();
              const file = item.getAsFile();
              if (!file) return true;
              uploadImage(file).then((url) => {
                const node = schema.nodes.image.create({ src: url, alt: file.name });
                const tr = view.state.tr.replaceSelectionWith(node);
                view.dispatch(tr);
              });
              return true;
            }
          }
          return false;
        },
        drop(view, event) {
          const files = event.dataTransfer?.files;
          if (!files || files.length === 0) return false;
          const imageFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
          if (imageFiles.length === 0) return false;
          event.preventDefault();
          const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
          for (const file of imageFiles) {
            uploadImage(file).then((url) => {
              const node = schema.nodes.image.create({ src: url, alt: file.name });
              const insertPos = pos?.pos ?? view.state.doc.content.size;
              const tr = view.state.tr.insert(insertPos, node);
              view.dispatch(tr);
            });
          }
          return true;
        },
      },
    },
  });
}

interface MarkdownEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  height?: string;
  showMenu?: boolean;
  disabled?: boolean;
}

/**
 * Inner component that uses useEditorEventCallback for flowchart save,
 * which requires being inside a ProseMirror context.
 */
function FlowchartSaveHandler({
  open,
  onOpenChange,
  initialMermaid,
  editingNodePos,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMermaid?: string;
  editingNodePos: number | null;
  onSaved: () => void;
}) {
  const handleSave = useEditorEventCallback((view, mermaidSource: string) => {
    const { schema } = view.state;
    const codeBlock = schema.nodes.code_block;
    if (!codeBlock) return;

    const node = codeBlock.create(
      { params: "mermaid" },
      schema.text(mermaidSource)
    );

    if (editingNodePos !== null) {
      const existingNode = view.state.doc.nodeAt(editingNodePos);
      if (existingNode) {
        const tr = view.state.tr.replaceWith(
          editingNodePos,
          editingNodePos + existingNode.nodeSize,
          node
        );
        view.dispatch(tr);
      }
    } else {
      const { $from } = view.state.selection;
      const pos = $from.after($from.depth);
      const tr = view.state.tr.insert(pos, node);
      view.dispatch(tr);
    }

    view.focus();
    onOpenChange(false);
    onSaved();
  });

  const onSave = useCallback(
    (src: string) => { handleSave(src); },
    [handleSave]
  );

  return (
    <FlowchartEditorModal
      open={open}
      onOpenChange={onOpenChange}
      initialMermaid={initialMermaid}
      onSave={onSave}
    />
  );
}

export default function MarkdownEditor({
  value = "",
  onChange,
  height = "200px",
  showMenu = true,
  disabled = false,
}: MarkdownEditorProps) {
  const [flowchartOpen, setFlowchartOpen] = useState(false);
  const [editingMermaid, setEditingMermaid] = useState<string | undefined>(undefined);
  const [editingNodePos, setEditingNodePos] = useState<number | null>(null);

  // Stable refs for the mermaid plugin callbacks
  const onEditRef = useRef((pos: number, source: string) => {
    setEditingMermaid(source);
    setEditingNodePos(pos);
    setFlowchartOpen(true);
  });
  const onDeleteRef = useRef((pos: number) => {
    // Will be replaced after state is available
  });

  const [state, setState] = useState(() => {
    const doc = value ? editorMarkdownParser.parse(value) : undefined;
    return EditorState.create({
      schema: editorSchema,
      doc,
      plugins: [
        reactKeys(),
        history(),
        keymap({
          ...baseKeymap,
          Enter: chainCommands(
            splitListItem(editorSchema.nodes.list_item),
            baseKeymap.Enter
          ),
          "Mod-i": toggleMark(editorSchema.marks.em),
          "Mod-b": toggleMark(editorSchema.marks.strong),
          "Mod-Shift-c": toggleMark(editorSchema.marks.code),
          "Mod-z": undo,
          "Mod-Shift-z": redo,
          "Mod-y": redo,
        }),
        createMermaidNodeViewPlugin(
          (pos, source) => onEditRef.current(pos, source),
          (pos) => onDeleteRef.current(pos),
        ),
        createImagePastePlugin(editorSchema),
      ],
    });
  });

  // Keep delete ref updated with a function that dispatches
  const viewRef = useRef<any>(null);

  const isInitialLoadRef = useRef(true);
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      try {
        const doc = value ? editorMarkdownParser.parse(value) : undefined;
        const newState = EditorState.create({
          schema: editorSchema,
          doc,
          plugins: state.plugins,
        });
        setState(newState);
        lastValueRef.current = value;
      } catch (error) {
        console.error("Failed to parse markdown:", error);
      }
    }
    isInitialLoadRef.current = false;
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const dispatchTransaction = useCallback(
    (tr: Transaction) => {
      setState((prev) => {
        const newState = prev.apply(tr);

        if (tr.docChanged && onChange && !isInitialLoadRef.current) {
          let markdown = editorMarkdownSerializer.serialize(newState.doc);
          markdown = markdown.replace(/\n\n$/, '\n');
          lastValueRef.current = markdown;
          onChange(markdown);
        }

        return newState;
      });
    },
    [onChange]
  );

  return (
    <div className="w-full border rounded-lg bg-card flex flex-col" style={{ height }}>
      <ProseMirror
        className="ProseMirror flex-1 flex flex-col min-h-0"
        state={state}
        dispatchTransaction={disabled ? undefined : dispatchTransaction}
        editable={() => !disabled}
      >
        {showMenu && !disabled && (
          <SimpleEditorMenu
            onOpenFlowchart={(source, pos) => {
              setEditingMermaid(source);
              setEditingNodePos(pos);
              setFlowchartOpen(true);
            }}
            onUploadImage={uploadImage}
          />
        )}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <ProseMirrorDoc spellCheck={false} />
        </div>
        <DeleteHandler onDeleteRef={onDeleteRef} />
        {!disabled && (
          <FlowchartSaveHandler
            open={flowchartOpen}
            onOpenChange={setFlowchartOpen}
            initialMermaid={editingMermaid}
            editingNodePos={editingNodePos}
            onSaved={() => {
              setEditingMermaid(undefined);
              setEditingNodePos(null);
            }}
          />
        )}
      </ProseMirror>
    </div>
  );
}

/**
 * Tiny component inside ProseMirror context that wires up the delete callback.
 */
function DeleteHandler({ onDeleteRef }: { onDeleteRef: React.MutableRefObject<(pos: number) => void> }) {
  const doDelete = useEditorEventCallback((view, pos: number) => {
    const node = view.state.doc.nodeAt(pos);
    if (node) {
      const tr = view.state.tr.delete(pos, pos + node.nodeSize);
      view.dispatch(tr);
    }
  });

  onDeleteRef.current = (pos: number) => doDelete(pos);

  return null;
}
